import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { baht, clock } from '../lib/format.ts';
import { useBill } from '../bill.tsx';
import { useStore } from '../store.tsx';
import { AsyncButton, Field, Modal, useToast } from '../ui.tsx';
import type { Sale } from '../lib/types.ts';

/**
 * รายการบิลที่เปิดค้างอยู่ — หมวด "Bill" ในแถบล่าง
 *
 * ★ ลอกจาก screens/BillScreen.js ★
 *   · การ์ด 200×100 ขอบหนา 2px เรียงแบบ wrap
 *   · การ์ด [+] อยู่หน้าสุดเสมอ
 *   · สีตามประเภทบิล — ทานที่ร้าน ส้ม · กลับบ้าน ฟ้า · เดลิเวอรี่ เทา
 *   · ★ บิลเดลิเวอรี่แยกไปแถวล่างสูง 120px เลื่อนแนวนอน ★ ไม่ปนกับบิลในร้าน
 *   · ในการ์ด: ชื่อ 20px กลาง · คำอธิบาย 12px · แถวล่าง "เวลา" กับ "ราคา"
 *   · ปุ่มถังขยะบนหัวจอเปิดโหมดลบ แล้วการ์ดขึ้นกากบาทแดงมุมขวาบน
 */
export function BillGrid({
  deleteMode,
  onNewBill,
  onOpened,
}: {
  deleteMode: boolean;
  onNewBill: () => void;
  onOpened: () => void;
}) {
  const store = useStore();
  const bill = useBill();
  const toast = useToast();
  const [sales, setSales] = useState<Sale[] | null>(null);
  const [voidTarget, setVoidTarget] = useState<Sale | null>(null);
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const result = await api.get<{ sales: Sale[] }>('/sales', {
        branchId: store.branch.id,
        status: 'open',
      });
      setSales(result.sales);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'โหลดรายการบิลไม่สำเร็จ', 'crit');
      setSales([]);
    }
  }, [store.branch.id, toast]);

  useEffect(() => { void load(); }, [load]);

  const open = async (sale: Sale) => {
    if (deleteMode) return;
    await bill.loadSale(sale.id);
    if (sale.diningTableId !== null) {
      const table = store.tables.find((t) => t.id === sale.diningTableId);
      if (table) bill.setPayload({ tableName: table.name });
    }
    if (sale.deliveryChannelId !== null) {
      const channel = store.deliveryChannels.find((c) => c.id === sale.deliveryChannelId);
      if (channel) bill.setPayload({ deliveryName: channel.name });
    }
    onOpened();
  };

  const doVoid = async () => {
    if (!voidTarget || reason.trim() === '') return;
    try {
      await api.post(`/sales/${voidTarget.id}/void`, { reason: reason.trim() });
      toast('ยกเลิกบิลแล้ว แจ้งเจ้าของร้านเรียบร้อย', 'warn');
      setVoidTarget(null);
      setReason('');
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'ยกเลิกไม่สำเร็จ', 'crit');
    }
  };

  if (!sales) {
    return <div className="center-screen"><div className="spinner" /></div>;
  }

  const inShop = sales.filter((s) => s.billType !== 'delivery');
  const delivery = sales.filter((s) => s.billType === 'delivery');

  const card = (sale: Sale) => {
    const kind = sale.billType === 'dine_in' ? 'shop' : sale.billType === 'takeaway' ? 'home' : 'deli';
    let name = '';
    let desc = '';

    if (sale.billType === 'dine_in') {
      const table = store.tables.find((t) => t.id === sale.diningTableId);
      name = `โต๊ะ ${table?.name ?? '-'}`;
    } else if (sale.billType === 'takeaway') {
      name = 'กลับบ้าน';
      desc = sale.deliveryOrderRef;
    } else {
      name = sale.deliveryOrderRef || '-';
      desc = store.deliveryChannels.find((c) => c.id === sale.deliveryChannelId)?.name ?? '';
    }

    return (
      <button key={sale.id} className={`bill-card ${kind}`} onClick={() => void open(sale)}>
        {deleteMode && (
          <span
            className="bdel"
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              setVoidTarget(sale);
              setReason('');
            }}
          >
            ✕
          </span>
        )}
        <span className="bname">{name}</span>
        <span className="bdesc">{desc}</span>
        <span className="brow">
          <span>เวลา {clock(sale.openedAt)}</span>
          <span>ราคา {baht(sale.totalSatang)}</span>
        </span>
      </button>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div className="bill-wrap">
          <button className="bill-card add" onClick={onNewBill}>+</button>
          {inShop.map(card)}
        </div>
      </div>

      {/* ★ เดลิเวอรี่อยู่แถวล่างแยกต่างหาก ★ เหมือนของเดิม */}
      <div className="deli-strip">
        {delivery.length === 0 ? (
          <span style={{ color: 'var(--ink-3)', fontSize: '.85rem' }}>
            ยังไม่มีออเดอร์เดลิเวอรี่
          </span>
        ) : (
          delivery.map(card)
        )}
      </div>

      {voidTarget && (
        <Modal title="ยกเลิกบิล" onClose={() => setVoidTarget(null)}>
          {/* ★ เหตุผลบังคับกรอก ★ ระบบแจ้งเจ้าของร้านทันทีพร้อมเหตุผลนี้ */}
          <p className="lede">
            บิลที่ยกเลิกไม่นับเป็นยอดขาย และระบบจะแจ้งเจ้าของร้านทันทีพร้อมเหตุผล
          </p>
          <Field label="เหตุผล">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="เช่น ลูกค้าเปลี่ยนใจ / คีย์ผิด"
              autoFocus
            />
          </Field>
          <div className="action-row">
            <button className="btn ghost" onClick={() => setVoidTarget(null)}>ไม่ยกเลิก</button>
            <AsyncButton className="btn danger" onClick={doVoid} disabled={reason.trim() === ''}>
              ยืนยันยกเลิก
            </AsyncButton>
          </div>
        </Modal>
      )}
    </div>
  );
}
